import { useState, useEffect, useCallback, useMemo } from 'react';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const CALENDAR_NAME = 'Family';
const CALENDAR_OWNER = 'marc@defineholy.com';
// calendarView is paged. A month of daily recurrences blows past one page,
// so we follow @odata.nextLink (same pattern as figApi.getItems). MAX_PAGES
// is a safety stop, not a target — 20 x 250 = 5,000 occurrences per view.
const PAGE_SIZE = 250;
const MAX_PAGES = 20;

// A runway is an Outlook category "Runway<N>" on an event: the month grid
// paints a lead-up band from (start - N days) through the event day. Nothing
// is stored but the category, so the band follows the event wherever it goes.
const RUNWAY_RE = /^Runway(\d+)$/;
const RUNWAY_MAX_DAYS = 14;
const MS_DAY = 86400000;

// N from the event's Runway category, or 0 if none / unrecognized.
export function runwayDaysOf(ev) {
  for (const c of ev?.categories || []) {
    const m = RUNWAY_RE.exec(c);
    if (m) return parseInt(m[1], 10) || 0;
  }
  return 0;
}

// Runways (from the hook's span list) covering `date`, soonest event first,
// then by event start time so same-day events read in schedule order.
export function coveringRunways(runways, date) {
  const d = dayStart(date);
  return runways
    .filter(r => d >= r.from && d <= r.to)
    .map(r => ({ event: r.event, total: r.total, daysRemaining: Math.round((r.to - d) / MS_DAY) }))
    .sort((a, b) => a.daysRemaining - b.daysRemaining || a.event.start - b.event.start);
}

// "Routine" = a recurring occurrence with no location and no attendees:
// daily self-directed habits (practice, language, solo swim, work blocks).
// Month view collapses these to a count; week view shows everything.
// Exceptions and one-offs are never routine, so a "NO SWIM — CANCELLED"
// single instance always stays visible.
export function isRoutine(ev) {
  return ev.type === 'occurrence' && !ev.location && (ev.attendees || []).length === 0;
}

function dayStart(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

let cachedCalendarId = null;
let cachedCalendarBase = null; // '/me' or '/users/marc@defineholy.com'
let cachedForUser = null;

function getWeekBounds(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - day);
  sunday.setHours(0, 0, 0, 0);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  saturday.setHours(23, 59, 59, 999);
  return { monday: sunday, sunday: saturday }; // keep property names for compatibility
}

function getDaysOfWeek(weekStart) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }
  return days;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function getMonthBounds(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { first, last };
}

export function useCalendar(getToken, userEmail) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [monthOffset, setMonthOffset] = useState(0);

  const today = new Date();

  // Week calculations
  const refDate = new Date(today);
  refDate.setDate(today.getDate() + weekOffset * 7);
  const { monday, sunday } = getWeekBounds(refDate);
  const weekDays = getDaysOfWeek(monday);

  // Month calculations
  const monthRef = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthYear = monthRef.getFullYear();
  const monthMonth = monthRef.getMonth();
  const { first: monthStart, last: monthEnd } = getMonthBounds(monthYear, monthMonth);

  // Build month grid — pad to full weeks starting Sunday
  const monthGridDays = (() => {
    const days = [];
    const startDay = monthStart.getDay(); // 0=Sun, already correct for Sunday-start
    for (let i = 0; i < startDay; i++) {
      const d = new Date(monthYear, monthMonth, -startDay + i + 1);
      days.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= monthEnd.getDate(); d++) {
      days.push({ date: new Date(monthYear, monthMonth, d), inMonth: true });
    }
    while (days.length % 7 !== 0) {
      const idx = days.length - days.filter(dd => dd.inMonth).length - days.filter(dd => !dd.inMonth && dd.date < monthStart).length + 1;
      days.push({ date: new Date(monthYear, monthMonth + 1, idx), inMonth: false });
    }
    return days;
  })();

  // Keep selectedDate within the current week view (week mode only)
  useEffect(() => {
    if (viewMode === 'week' && (selectedDate < monday || selectedDate > sunday)) {
      const todayInWeek = weekDays.find(d => isSameDay(d, today));
      setSelectedDate(todayInWeek || monday);
    }
  }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();

      // Clear cache if a different user logged in
      if (userEmail && cachedForUser && cachedForUser !== userEmail) {
        cachedCalendarId = null;
        cachedCalendarBase = null;
        cachedForUser = null;
      }

      // Find the Family calendar — try own calendars first, then Marc's shared calendar
      if (!cachedCalendarId) {
        // Try logged-in user's calendars
        const calRes = await fetch(`${GRAPH}/me/calendars?$select=name,id&$top=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (calRes.ok) {
          const calData = await calRes.json();
          console.log('[Calendar] /me/calendars returned:', (calData.value || []).map(c => ({ name: c.name, id: c.id })));
          const familyCal = (calData.value || []).find(
            c => c.name.toLowerCase() === CALENDAR_NAME.toLowerCase()
          );
          if (familyCal) {
            cachedCalendarId = familyCal.id;
            cachedCalendarBase = '/me';
          }
        }

        // Fallback: read from Marc's shared calendar
        if (!cachedCalendarId) {
          console.log('[Calendar] Family not found on /me, trying shared from', CALENDAR_OWNER);
          const sharedRes = await fetch(`${GRAPH}/users/${CALENDAR_OWNER}/calendars?$select=name,id&$top=50`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (sharedRes.ok) {
            const sharedData = await sharedRes.json();
            console.log('[Calendar] Shared calendars found:', (sharedData.value || []).map(c => c.name));
            const familyCal = (sharedData.value || []).find(
              c => c.name.toLowerCase() === CALENDAR_NAME.toLowerCase()
            );
            if (familyCal) {
              cachedCalendarId = familyCal.id;
              cachedCalendarBase = `/users/${CALENDAR_OWNER}`;
              console.log('[Calendar] Using shared Family calendar from', CALENDAR_OWNER);
            }
          } else {
            console.warn('[Calendar] Shared calendar fetch failed:', sharedRes.status, await sharedRes.text().catch(() => ''));
          }
        }

        // Final fallback: try Marc's default calendar directly
        if (!cachedCalendarId) {
          console.log('[Calendar] Trying Marc default calendar as last resort');
          const defaultRes = await fetch(`${GRAPH}/users/${CALENDAR_OWNER}/calendar`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (defaultRes.ok) {
            const defaultCal = await defaultRes.json();
            // Use Marc's default calendar if no named Family calendar exists
            cachedCalendarId = defaultCal.id;
            cachedCalendarBase = `/users/${CALENDAR_OWNER}`;
            console.log('[Calendar] Falling back to Marc default calendar:', defaultCal.name);
          } else {
            console.warn('[Calendar] Default calendar fetch failed:', defaultRes.status);
          }
        }

        if (!cachedCalendarId) {
          throw new Error(`Calendar "${CALENDAR_NAME}" not found. Check that the Family calendar is shared to your account.`);
        }
        cachedForUser = userEmail || null;
      }

      // Fetch events for the visible date range
      // Read RUNWAY_MAX_DAYS past the visible range so a runway leading into
      // an event just beyond it still paints across the visible tail.
      const rangeStart = viewMode === 'month' ? monthStart : monday;
      const visibleEnd = viewMode === 'month' ? monthEnd : sunday;
      const rangeEnd = new Date(visibleEnd.getTime() + RUNWAY_MAX_DAYS * MS_DAY);
      const startISO = rangeStart.toISOString();
      const endISO = rangeEnd.toISOString();
      const headers = {
        Authorization: `Bearer ${token}`,
        Prefer: 'outlook.timezone="America/Chicago"',
      };

      let url =
        `${GRAPH}${cachedCalendarBase}/calendars/${cachedCalendarId}/calendarView` +
        `?startDateTime=${startISO}&endDateTime=${endISO}` +
        `&$select=id,subject,start,end,location,bodyPreview,body,categories,isAllDay,attendees,type,seriesMasterId` +
        `&$orderby=start/dateTime` +
        `&$top=${PAGE_SIZE}`;
      const allEvents = [];
      let pages = 0;
      while (url) {
        if (pages >= MAX_PAGES) {
          console.warn(`[Calendar] Hit MAX_PAGES (${MAX_PAGES}) for ${startISO}..${endISO}; results truncated at ${allEvents.length} events`);
          break;
        }
        const eventsRes = await fetch(url, { headers });
        if (!eventsRes.ok) {
          const body = await eventsRes.text();
          throw new Error(`Failed to load events (${eventsRes.status}): ${body}`);
        }
        const eventsData = await eventsRes.json();
        allEvents.push(...(eventsData.value || []));
        url = eventsData['@odata.nextLink'] || null;
        pages++;
      }

      const mapped = allEvents.map(ev => ({
        id: ev.id,
        title: ev.subject || '',
        start: ev.start?.dateTime ? new Date(ev.start.dateTime) : null,
        end: ev.end?.dateTime ? new Date(ev.end.dateTime) : null,
        location: ev.location?.displayName || '',
        address: ev.location?.address
          ? [ev.location.address.street, ev.location.address.city, ev.location.address.state].filter(Boolean).join(', ')
          : '',
        notes: ev.bodyPreview || '',
        allDay: !!ev.isAllDay,
        categories: ev.categories || [],
        type: ev.type || 'singleInstance',
        seriesMasterId: ev.seriesMasterId || null,
        attendees: ev.attendees || [],
      }));

      setEvents(mapped);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, viewMode, monday.toISOString(), sunday.toISOString(), monthYear, monthMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const eventsForDate = (date) => {
    return events.filter(ev => ev.start && isSameDay(ev.start, date));
  };

  const selectedEvents = eventsForDate(selectedDate);

  // Inclusive [from, to] day spans for every runway-tagged event in view.
  const runways = useMemo(() => events.flatMap(ev => {
    const n = runwayDaysOf(ev);
    if (!n || !ev.start) return [];
    const to = dayStart(ev.start);
    const from = new Date(to);
    from.setDate(to.getDate() - n);
    return [{ event: ev, total: n, from, to }];
  }), [events]);

  // Every runway covering `date`, nearest first (week view stacks them all).
  const runwaysForDate = (date) => coveringRunways(runways, date);

  // The nearest runway covering `date`, plus how many others also cover it.
  // Null when the day is on no runway. Month cells show only this one.
  const runwayForDate = (date) => {
    const hits = coveringRunways(runways, date);
    if (hits.length === 0) return null;
    return { ...hits[0], others: hits.length - 1 };
  };

  // Optimistic local edit; callers refetch afterwards to reconcile with Graph.
  const patchEventLocal = (id, patch) => {
    setEvents(prev => prev.map(ev => ev.id === id ? { ...ev, ...patch } : ev));
  };

  return {
    events,
    selectedEvents,
    eventsForDate,
    runwayForDate,
    runwaysForDate,
    patchEventLocal,
    selectedDate,
    setSelectedDate,
    weekDays,
    weekOffset,
    setWeekOffset,
    monday,
    today,
    loading,
    error,
    isSameDay,
    refetch: fetchEvents,
    viewMode,
    setViewMode,
    monthOffset,
    setMonthOffset,
    monthGridDays,
    monthYear,
    monthMonth,
    getCalendarPath: () => cachedCalendarBase && cachedCalendarId
      ? { base: cachedCalendarBase, id: cachedCalendarId }
      : null,
  };
}
